import z from "zod";
import { HomeownerInterestSchema, ProductTrackSchema } from "lib/misc/projectProducts";
import { LeadSegmentSchema } from "lib/misc/leadSegments";
import { CrmActivityTypeEnum } from "types/Project/crmActivity";

export const LeadReadinessSchema = z.enum(["cold", "warm", "hot", "abandoned"]);

const AddressSchema = z
  .object({
    addressStreet: z.string().nullish(),
    addressUnit: z.string().nullish(),
    addressCity: z.string().nullish(),
    addressState: z.string().nullish(),
    addressZip: z.string().nullish(),
    addressCountry: z.string().nullish(),
  })
  .passthrough();

const HomeownerSchema = z
  .object({
    leadId: z.number().nullish(),
    leadFirstName: z.string().nullish(),
    leadLastName: z.string().nullish(),
    leadEmail: z.string().nullish(),
    leadPhone: z.string().nullish(),
    leadCreatedAt: z.string().nullish(),
    address: AddressSchema.nullish(),
  })
  .passthrough();

const ProjectUserSchema = z
  .object({
    id: z.number(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.string().nullish(),
    phone: z.string().nullish(),
    avatar: z
      .object({
        thumb: z.string().nullish(),
      })
      .passthrough()
      .nullish(),
    role: z.string().nullish(),
  })
  .passthrough();

const ActivitySchema = z
  .object({
    kind: z.nativeEnum(CrmActivityTypeEnum),
  })
  .passthrough();

const OfferSchema = z
  .object({
    id: z.number().nullish(),
    projectId: z.number().nullish(),
    receiverId: z.number().nullish(),
  })
  .passthrough();

const OutreachSchema = z.object({
  lastSentAt: z.string().nullish(),
  nextScheduledAt: z.string().nullish(),
});

export const LeadsTableProjectSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    leadId: z.number(),
    leadFirstName: z.string().nullish(),
    leadLastName: z.string().nullish(),
    leadEmail: z.string().nullish(),
    leadSource: z.string().nullish(),
    homeownerInterest: HomeownerInterestSchema.optional(),
    leadSegment: LeadSegmentSchema,
    productTracks: z.array(ProductTrackSchema).optional(),
    projectId: z.number().nullish(),
    projectMentorId: z.number().nullish(),
    passedStageNames: z
      .array(z.string())
      .nullish()
      .transform((value) => value ?? []),
    activities: z
      .array(ActivitySchema)
      .nullish()
      .transform((value) => value ?? []),
    closeAt: z.string().nullish(),
    hasCurrentOffer: z
      .boolean()
      .nullish()
      .transform((value) => value ?? false),
    currentOfferId: z.number().nullish(),
    currentOfferReceiverId: z.number().nullish(),
    offer: OfferSchema.nullish(),
    readiness: LeadReadinessSchema,
    readinessScore: z.number().nullish(),
    milestoneDisplayName: z.string().nullish(),
    projectStageMilestoneName: z.string().nullish(),
    coalescedUpdatedAt: z.string().nullish(),
    homeowner: HomeownerSchema.nullish(),
    projectUsers: z
      .array(ProjectUserSchema)
      .nullish()
      .transform((value) => value ?? []),
    leadGroups: z
      .array(z.object({ id: z.number(), name: z.string() }))
      .nullish()
      .transform((value) => value ?? []),
    outreach: OutreachSchema.nullish().transform((value) => value ?? { lastSentAt: null, nextScheduledAt: null }),
  })
  .passthrough();

export const LeadsTableResponseSchema = z.object({
  projects: z.array(LeadsTableProjectSchema),
  paging: z.object({
    itemCount: z.number().nullable(),
    hasMore: z.boolean().optional(),
    pageNumber: z.number(),
    pageSize: z.number(),
  }),
});

export const AbandonLeadResultSchema = z
  .object({
    leadId: z.number(),
    readiness: LeadReadinessSchema,
    previousReadiness: LeadReadinessSchema.nullable().default(null),
    abandonedAt: z.string().nullable().default(null),
  })
  .passthrough();

export const ReopenAbandonedLeadResultSchema = z
  .object({
    leadId: z.number(),
    readiness: LeadReadinessSchema,
  })
  .passthrough();
